import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import requestHandler from '../../utils/requestHandler';

// Diagnosis entry for lab orders. When the deployment enables the ICD-11
// catalog (features['lis.icd11Diagnosis'], on by default) this is a debounced
// WHO ICD-11 autocomplete that ALSO accepts free text (freeSolo) for diagnoses
// not in the catalog; otherwise it falls back to a plain free-text field.
//
// The stored value is a single string: a picked ICD-11 entry becomes
// "CODE — Title", free text is kept verbatim. That keeps the order model and
// report unchanged — callers just read/write `value`.
export default function DiagnosisInput({ value = '', onChange, label, placeholder = 'Diagnosis', size = 'small', sx }) {
    const icd11Enabled = useSelector(
        (state) => state.hospitalConfig?.features?.['lis.icd11Diagnosis'] !== false
    );
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Debounced ICD-11 search. Codes prefix-match, titles substring-match
    // (server-side); 2+ chars required so a single char can't match thousands.
    useEffect(() => {
        if (!icd11Enabled) return undefined;
        const q = (value || '').trim();
        if (q.length < 2) { setOptions([]); return undefined; }
        let cancelled = false;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const authToken = localStorage.getItem('item');
                const currentBranch = JSON.parse(localStorage.getItem('currentBranch') || 'null');
                const res = await requestHandler(
                    `${process.env.REACT_APP_SERVER_API}api/lis/icd11?q=${encodeURIComponent(q)}&limit=25`,
                    { method: 'GET', headers: { Authorization: authToken, 'Content-Type': 'application/json', 'X-Current-Branch': currentBranch?.id || '' } }
                );
                if (!cancelled) setOptions(res?.data || []);
            } catch {
                if (!cancelled) setOptions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [value, icd11Enabled]);

    if (!icd11Enabled) {
        return (
            <TextField
                fullWidth
                size={size}
                sx={sx}
                label={label}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
            />
        );
    }

    const labelFor = (o) => (typeof o === 'string' ? o : `${o.code} — ${o.title}`);

    return (
        <Autocomplete
            freeSolo
            fullWidth
            size={size}
            sx={sx}
            options={options}
            loading={loading}
            filterOptions={(x) => x}
            inputValue={value}
            onInputChange={(_, v) => onChange?.(v)}
            getOptionLabel={labelFor}
            noOptionsText={
                (value || '').trim().length < 2
                    ? 'Type 2+ characters to search ICD-11'
                    : loading ? 'Searching…' : 'No ICD-11 match — the text you typed is saved as-is'
            }
            renderOption={(props, opt) => (
                <Box component="li" {...props} key={opt.code}>
                    <Box sx={{ display: 'flex', gap: 1.5, width: '100%', minWidth: 0, alignItems: 'baseline' }}>
                        <Typography sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5, fontWeight: 600, flexShrink: 0, minWidth: 64, whiteSpace: 'nowrap' }}>
                            {opt.code}
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, minWidth: 0 }}>{opt.title}</Typography>
                    </Box>
                </Box>
            )}
            renderInput={(p) => (
                <TextField
                    {...p}
                    label={label}
                    placeholder={`${placeholder} — search ICD-11 or type free text`}
                    InputProps={{
                        ...p.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={14} /> : null}
                                {p.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
